/**
 * Hydrate off-screen page-1 PLP tiles from this section's own Section Rendering response.
 *
 * Each request is `?section_id=<main section>&limit=12&page=N` on the current URL, so filters
 * and sort carry over and the cards come from the real `_product-card` block pipeline with
 * every app block (Badge Manager, Budget Pay) in place. main-collection.liquid never defers
 * when `limit` is in the URL, so these responses are always full cards.
 *
 * N-GRIDDOC, two theme settings, both on by default (with both off this script behaves exactly
 * as before); sections/main-collection.liquid prints an attribute on the grid for each one on:
 * - perf_plp_tile_retry_backoff (data-plp-retry-backoff): a failed request is retried with a
 *   growing delay (1 s doubling to 30 s) while its tiles are still near the screen, instead of at
 *   once on the next frame, and given up after 7 failures for the same page (its tiles are then
 *   marked failed until the next filter change or load, or until the browser reports the
 *   connection back: FL-F5, retryGivenUpTiles). Off: retried on the next frame.
 * - perf_plp_tile_settled_height (data-plp-settled-height): the grid carries the height of the
 *   tallest server-rendered card (--plp-tile-height), which main-collection.css gives to every
 *   placeholder and to every card that replaces one, so a batch landing on screen does not push
 *   the grid down. Off: no height is set.
 *
 * R7-08 TILE-HEIGHT, theme setting perf_plp_tile_card_settled (data-plp-card-settled, on by
 * default; off, this script behaves exactly as before): a loaded card with the rating overlay
 * gets its stars put on the picture before it replaces its placeholder (settleRatingOverlay), so
 * it is first drawn at its settled height.
 *
 * R7-09 GRID-LATE, theme setting perf_plp_late_card_images_near (data-plp-late-images, off by
 * default until a lab check passes; off, this script behaves exactly as before): a card loaded
 * before the shopper's first scroll, wheel, touch, pointer press or key, whose placeholder is
 * wholly below the screen, keeps its picture (holdCardPicture) until that first move or until it
 * comes into the screen, whichever is first (releaseHeldPicture). A visit with no move loads no
 * picture it never shows.
 */
const GRID_SELECTOR = '.main-collection__grid';
const TILE_SELECTOR = '.product-grid__item[data-lazy-tile]';
const FILTER_UPDATE_EVENT = 'filter:update';
// Must be one of the section's standard page sizes (12, 24, 36, 48, 60, 120).
const HYDRATE_PAGE_SIZE = 12;
// Retry timing for a failed card request. Chosen values, not measured, from one rule: keep
// trying for about a minute, doubling the wait each time up to a ceiling of 30 s, which keeps a
// tile that stays near the screen to at most two requests a minute. The limit of 7 is where
// the doubling reaches that ceiling (waits of 1, 2, 4, 8, 16, then 30 s), so the page gives up
// rather than repeat the ceiling; and the first wait of 1 s makes those waits add up to about
// a minute (1 + 2 + 4 + 8 + 16 + 30 = 61 s) while still letting a brief network drop or a
// one-off server error clear.
// Every failed request counts once towards its page's limit of 7, whichever group of that
// page's tiles sent it, and each group waits on its own timer. One group alone waits 1, 2, 4,
// 8, 16 and 30 s (about 61 s from the first request to the 7th); when a page's tiles are asked
// for in two groups, the groups share the count and wait side by side, so the limit comes
// sooner (production, every card request refused: 7 requests in 21.6 s). Offline with every
// card request failing, the old immediate retry sent 1,799 requests in 20 s.
const RETRY_FIRST_MS = 1000;
const RETRY_MAX_MS = 30000;
const RETRY_MAX_FAILURES = 7;

let tileObserver = null;
let gridObserver = null;
let pageRequests = new Map();
const queuedTiles = new Set();
const nearbyTiles = new Set();
let batchFrame = 0;
let flushing = false;
let generation = 0;
let requestController = null;
let pageFailures = new Map();
const givenUpTiles = new Set();
let settledHeightObserver = null;
// R7-09: the same one pixel GIF the card's second picture carries before it is asked for.
const HELD_PICTURE_SRC = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
const SHOPPER_MOVES = ['scroll', 'wheel', 'touchstart', 'pointerdown', 'keydown'];
// 'scroll' is the page's own scroll only (not captured: an element's scroll event does not reach
// the window), so a slider that scrolls itself (cx-card-slider.js auto scroll) is not taken for
// the shopper; the other four are captured, so the shopper's input on any element counts.
const shopperMoveOptions = (type) => ({ capture: type !== 'scroll', passive: true });
let shopperMoved = false;
let shopperMoveWatched = false;
const heldPictures = new Set();
let heldPictureObserver = null;

/** Yield between card connections; abort also releases a frame queued in a background tab. */
function nextFrame(signal) {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    let frame = 0;
    const done = () => {
      signal.removeEventListener('abort', cancel);
      resolve();
    };
    const cancel = () => {
      cancelAnimationFrame(frame);
      done();
    };
    frame = requestAnimationFrame(done);
    signal.addEventListener('abort', cancel, { once: true });
  });
}

function copyTileState(tile, loadedItem) {
  if (tile.id) loadedItem.id = tile.id;
  ['page', 'imageLoading', 'fetchpriority', 'imageMain'].forEach((key) => {
    if (tile.dataset[key]) loadedItem.dataset[key] = tile.dataset[key];
  });
  loadedItem.dataset.lazyTileLoaded = 'true';
}

/**
 * R7-08: the move assets/collection-filters.js makes in applyRatingOverlayOnImage, made on a loaded
 * card before it is in the page. That script moves the stars from under the title onto the
 * picture from a MutationObserver, one animation frame after the card is put in, so each loaded
 * card was drawn once with the stars in the flow (about 10 px taller than its placeholder), then
 * shrank: two small layout shifts of the rows below, per row. The script still sees the card and
 * finds the stars already in place. Same selectors and the same check as that function; the grid
 * is always inside .main-collection, which its selector also asks for.
 */
function settleRatingOverlay(card) {
  card.querySelectorAll('.product-card[data-rating-overlay-on-image="true"] .product-card__content').forEach((content) => {
    const reviewBlock = content.querySelector('.review-block');
    const gallery = content.querySelector('.card-gallery');
    if (!reviewBlock || !gallery || reviewBlock.parentElement === gallery) return;
    reviewBlock.classList.add('card-gallery__rating-overlay');
    gallery.appendChild(reviewBlock);
  });
}

/**
 * R7-09: the card's first picture (not the second, which the card already holds in data
 * attributes), only when it is lazy: its src and every srcset of its picture move to
 * data-plp-held-src and data-plp-held-srcset, and the img shows the one pixel GIF meanwhile.
 * Returns the img, or null when nothing was held.
 */
function holdCardPicture(card) {
  const img = card.querySelector('.card-gallery picture:not([data-deferred-card-picture]) > img.product-media__image[loading="lazy"][src]');
  if (!img || img.getAttribute('src') === HELD_PICTURE_SRC) return null;
  img.parentElement.querySelectorAll('source[srcset]').forEach((source) => {
    source.dataset.plpHeldSrcset = source.getAttribute('srcset');
    source.removeAttribute('srcset');
  });
  if (img.hasAttribute('srcset')) {
    img.dataset.plpHeldSrcset = img.getAttribute('srcset');
    img.removeAttribute('srcset');
  }
  img.dataset.plpHeldSrc = img.getAttribute('src');
  img.setAttribute('src', HELD_PICTURE_SRC);
  return img;
}

/** R7-09: gives a held picture its sources back; a source or src another script set since is kept. */
function releaseHeldPicture(img) {
  heldPictures.delete(img);
  heldPictureObserver?.unobserve(img);
  if (!img.hasAttribute('data-plp-held-src')) return;
  img.parentElement?.querySelectorAll('source[data-plp-held-srcset]').forEach((source) => {
    if (!source.hasAttribute('srcset')) source.setAttribute('srcset', source.dataset.plpHeldSrcset);
    source.removeAttribute('data-plp-held-srcset');
  });
  if (img.dataset.plpHeldSrcset && !img.hasAttribute('srcset')) img.setAttribute('srcset', img.dataset.plpHeldSrcset);
  if (img.getAttribute('src') === HELD_PICTURE_SRC) img.setAttribute('src', img.dataset.plpHeldSrc);
  img.removeAttribute('data-plp-held-src');
  img.removeAttribute('data-plp-held-srcset');
}

function onShopperMove() {
  shopperMoved = true;
  SHOPPER_MOVES.forEach((type) => window.removeEventListener(type, onShopperMove, shopperMoveOptions(type)));
  [...heldPictures].forEach(releaseHeldPicture);
  heldPictureObserver?.disconnect();
}

/** R7-09: listens once for the shopper's first move, and releases a held picture that comes into the screen. */
function watchShopperMove() {
  if (shopperMoveWatched) return;
  shopperMoveWatched = true;
  // A move made before this module ran: the page is already scrolled, so nothing is held.
  if ((document.scrollingElement?.scrollTop || 0) > 0) {
    shopperMoved = true;
    return;
  }
  SHOPPER_MOVES.forEach((type) => window.addEventListener(type, onShopperMove, shopperMoveOptions(type)));
  heldPictureObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => { if (entry.isIntersecting) releaseHeldPicture(entry.target); });
  }, { rootMargin: '0px', threshold: 0 });
}

function markFailed(tile) {
  tile.dataset.lazyTileLoaded = 'error';
  tile.removeAttribute('aria-busy');
  tile.classList.add('product-grid__item--deferred-error');
}

/** Stop asking for these tiles: mark them failed and stop observing them. */
function giveUpTiles(tiles) {
  tiles.forEach((tile) => {
    if (!tile.isConnected) return;
    markFailed(tile);
    tileObserver?.unobserve(tile);
    nearbyTiles.delete(tile);
    givenUpTiles.add(tile);
  });
}

/**
 * FL-F5: the browser reports the connection back (the window's `online` event, fired when it
 * goes from offline to online). The pages given up after RETRY_MAX_FAILURES start again with a
 * fresh count, and their given up tiles go back to placeholders and are observed again, so a tile
 * near the screen asks once more, with the same back off. Only tiles given up by the back off are
 * taken (a tile marked failed because the response had no card for it stays failed). Without the
 * back off nothing is ever given up, so this does nothing.
 */
function retryGivenUpTiles() {
  if (!givenUpTiles.size) return;
  pageFailures.forEach((failures, key) => {
    if (failures >= RETRY_MAX_FAILURES) pageFailures.delete(key);
  });
  givenUpTiles.forEach((tile) => {
    if (!tile.isConnected || tile.dataset.lazyTileLoaded !== 'error') return;
    tile.dataset.lazyTileLoaded = '';
    tile.classList.remove('product-grid__item--deferred-error');
    tile.setAttribute('aria-busy', 'true');
    tileObserver?.observe(tile);
  });
  givenUpTiles.clear();
}

/**
 * Refresh newly connected badge hosts within this grid. Retain legacy app fallbacks.
 */
let badgeSyncTimer = 0;
function syncAppBadges(grid) {
  window.clearTimeout(badgeSyncTimer);
  badgeSyncTimer = window.setTimeout(() => {
    if (!grid.isConnected) return;
    if (typeof window.BadgeManager?.refresh === 'function') window.BadgeManager.refresh(grid);
    else if (typeof window.BadgeManager?.refreshPlpHosts === 'function') window.BadgeManager.refreshPlpHosts();
    else window.dispatchEvent(new Event('resize'));
  }, 50);
}

function hydratePageUrl(sectionId, page) {
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.delete('view');
  url.searchParams.set('section_id', sectionId);
  url.searchParams.set('limit', String(HYDRATE_PAGE_SIZE));
  url.searchParams.set('page', String(page));
  return url.toString();
}

/** One fetch per hydration page, shared by every tile on it. Resolves to a product id → card map. */
function requestPage(sectionId, page, signal) {
  const key = `${sectionId}:${page}`;
  const cache = pageRequests;
  let request = cache.get(key);
  if (request) return request;

  request = fetch(hydratePageUrl(sectionId, page), { signal })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    })
    .then((html) => {
      if (signal.aborted) throw new DOMException('Hydration superseded', 'AbortError');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = doc.querySelectorAll(`${GRID_SELECTOR} > .product-grid__item[data-product-id]:not([data-lazy-tile])`);
      return new Map([...items].map((item) => [item.dataset.productId, item]));
    });
  // Drop failures so a later scroll can retry.
  request.catch(() => cache.delete(key));
  cache.set(key, request);
  return request;
}

async function hydrateTiles(grid, page, tiles, signal) {
  const sectionId = grid.dataset.plpDeferredSectionId?.trim();
  if (!sectionId) {
    tiles.forEach(markFailed);
    return;
  }

  const backoff = grid.hasAttribute('data-plp-retry-backoff');
  const settleCards = grid.hasAttribute('data-plp-card-settled') && grid.closest('.main-collection') !== null;
  const holdLatePictures = grid.hasAttribute('data-plp-late-images');
  const failureKey = `${sectionId}:${page}`;
  if (backoff && (pageFailures.get(failureKey) || 0) >= RETRY_MAX_FAILURES) {
    giveUpTiles(tiles);
    return;
  }

  let mounted = false;
  try {
    const loadedItems = await requestPage(sectionId, page, signal);
    pageFailures.delete(failureKey);
    for (const tile of tiles) {
      await nextFrame(signal);
      if (signal.aborted || !grid.isConnected) return;
      if (!tile.isConnected || !nearbyTiles.has(tile)) {
        tile.dataset.lazyTileLoaded = '';
        continue;
      }
      const loadedItem = loadedItems.get(tile.dataset.productId);
      if (!(loadedItem instanceof HTMLElement)) {
        markFailed(tile);
        tileObserver?.unobserve(tile);
        nearbyTiles.delete(tile);
        continue;
      }
      const card = document.importNode(loadedItem, true);
      copyTileState(tile, card);
      if (settleCards) settleRatingOverlay(card);
      // R7-09: read before the swap; layout is clean at the start of this frame.
      const heldImg = holdLatePictures && !shopperMoved && tile.getBoundingClientRect().top >= window.innerHeight ? holdCardPicture(card) : null;
      tileObserver?.unobserve(tile);
      nearbyTiles.delete(tile);
      queuedTiles.delete(tile);
      tile.replaceWith(card);
      if (heldImg) {
        heldPictures.add(heldImg);
        heldPictureObserver?.observe(heldImg);
      }
      mounted = true;
    }
  } catch (error) {
    if (signal.aborted) return;
    console.warn('Collection PLP deferred tiles failed:', error);
    if (!backoff) {
      tiles.forEach((tile) => {
        if (!tile.isConnected) return;
        tile.dataset.lazyTileLoaded = '';
        tileObserver?.unobserve(tile);
        tileObserver?.observe(tile);
      });
      return;
    }
    // Back off: the tiles sit out a growing delay, then are observed again, so a tile still
    // near the screen asks once more. Re-observing at once sent the request again every frame.
    const failures = (pageFailures.get(failureKey) || 0) + 1;
    pageFailures.set(failureKey, failures);
    if (failures >= RETRY_MAX_FAILURES) {
      giveUpTiles(tiles);
      return;
    }
    const delay = Math.min(RETRY_FIRST_MS * 2 ** (failures - 1), RETRY_MAX_MS);
    const currentGeneration = generation;
    tiles.forEach((tile) => {
      if (!tile.isConnected) return;
      tile.dataset.lazyTileLoaded = 'retry';
      tileObserver?.unobserve(tile);
      nearbyTiles.delete(tile);
    });
    window.setTimeout(() => {
      if (currentGeneration !== generation) return;
      tiles.forEach((tile) => {
        if (!tile.isConnected || tile.dataset.lazyTileLoaded !== 'retry') return;
        tile.dataset.lazyTileLoaded = '';
        tileObserver?.observe(tile);
      });
    }, delay);
  } finally {
    if (mounted && grid.isConnected) syncAppBadges(grid);
  }
}

async function flushQueue() {
  batchFrame = 0;
  if (flushing || !requestController) return;
  flushing = true;
  const currentGeneration = generation;
  const signal = requestController.signal;
  const byPage = new Map();
  queuedTiles.forEach((tile) => {
    if (!tile.isConnected || !nearbyTiles.has(tile)) return;
    const grid = tile.closest(GRID_SELECTOR);
    const index = Number.parseInt(tile.dataset.plpIndex ?? '', 10);
    if (!(grid instanceof HTMLElement) || Number.isNaN(index)) {
      markFailed(tile);
      return;
    }
    const page = Math.floor(index / HYDRATE_PAGE_SIZE) + 1;
    if (!byPage.has(page)) byPage.set(page, { grid, tiles: [] });
    byPage.get(page).tiles.push(tile);
  });
  queuedTiles.clear();
  try {
    // One Section Rendering request at a time, with one card connection per frame.
    for (const [page, { grid, tiles }] of byPage) {
      if (signal.aborted) return;
      const visibleTiles = tiles.filter((tile) => tile.isConnected && nearbyTiles.has(tile));
      if (!visibleTiles.length) continue;
      visibleTiles.forEach((tile) => { tile.dataset.lazyTileLoaded = 'loading'; });
      await hydrateTiles(grid, page, visibleTiles, signal);
    }
  } finally {
    if (currentGeneration === generation) {
      flushing = false;
      if (queuedTiles.size) scheduleQueue();
    }
  }
}

function scheduleQueue() {
  if (batchFrame || flushing) return;
  batchFrame = requestAnimationFrame(() => void flushQueue());
}

function queueTile(tile) {
  if (['loading', 'true', 'error', 'retry'].includes(tile.dataset.lazyTileLoaded)) return;
  queuedTiles.add(tile);
  scheduleQueue();
}

function observeTiles(root) {
  const tiles = root instanceof HTMLElement && root.matches(TILE_SELECTOR) ? [root] : root.querySelectorAll(TILE_SELECTOR);
  tiles.forEach((tile) => {
    if (tile instanceof HTMLElement && tile.dataset.lazyTileLoaded !== 'error') tileObserver?.observe(tile);
  });
}

/**
 * Keep --plp-tile-height on the grid at the height of the tallest server-rendered card, which
 * is the height a settled row has. ResizeObserver reports it after layout, so nothing here
 * forces a layout, and it follows font loading and viewport changes. Only product cards count:
 * grid banners (.product-grid__item--banner, no data-product-id) are left out, since an
 * HTML banner sized by its content would otherwise set the height of every deferred row.
 */
function watchSettledHeight(grid) {
  settledHeightObserver?.disconnect();
  settledHeightObserver = null;
  if (!grid.hasAttribute('data-plp-settled-height') || typeof ResizeObserver !== 'function') return;
  const cards = [...grid.querySelectorAll(':scope > .product-grid__item[data-product-id]:not([data-lazy-tile])')];
  if (!cards.length) return;
  const heights = new Map();
  settledHeightObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      const box = entry.borderBoxSize?.[0];
      heights.set(entry.target, box ? box.blockSize : entry.target.getBoundingClientRect().height);
    });
    const tallest = Math.ceil(Math.max(0, ...heights.values()));
    if (tallest > 0) {
      grid.style.setProperty('--plp-tile-height', `${tallest}px`);
      grid.setAttribute('data-plp-tile-height', '');
    }
  });
  cards.forEach((card) => settledHeightObserver.observe(card));
}

function watchGrid(grid) {
  gridObserver?.disconnect();
  gridObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node instanceof HTMLElement) observeTiles(node);
    }));
  });
  gridObserver.observe(grid, { childList: true });
}

export function initCollectionPlpDeferredTiles() {
  generation += 1;
  requestController?.abort();
  requestController = new AbortController();
  if (batchFrame) cancelAnimationFrame(batchFrame);
  batchFrame = 0;
  flushing = false;
  pageRequests = new Map();
  pageFailures = new Map();
  givenUpTiles.clear();
  queuedTiles.clear();
  nearbyTiles.clear();
  tileObserver?.disconnect();
  gridObserver?.disconnect();
  settledHeightObserver?.disconnect();
  const grid = document.querySelector(GRID_SELECTOR);
  if (!(grid instanceof HTMLElement)) return;
  const currentGeneration = generation;
  // A section morph can retain placeholders from an aborted request.
  grid.querySelectorAll(TILE_SELECTOR).forEach((tile) => {
    if (tile.dataset.lazyTileLoaded === 'loading' || tile.dataset.lazyTileLoaded === 'retry') tile.dataset.lazyTileLoaded = '';
  });
  watchSettledHeight(grid);
  if (grid.hasAttribute('data-plp-late-images') && !shopperMoved) watchShopperMove();
  tileObserver = new IntersectionObserver((entries) => {
    if (currentGeneration !== generation) return;
    entries.forEach((entry) => {
      if (!(entry.target instanceof HTMLElement)) return;
      if (!entry.isIntersecting) {
        nearbyTiles.delete(entry.target);
        queuedTiles.delete(entry.target);
        return;
      }
      nearbyTiles.add(entry.target);
      queueTile(entry.target);
    });
  }, { rootMargin: '200px 0px', threshold: 0 });
  observeTiles(grid);
  watchGrid(grid);
}

function scheduleInit() {
  requestAnimationFrame(initCollectionPlpDeferredTiles);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleInit, { once: true });
else scheduleInit();
document.addEventListener(FILTER_UPDATE_EVENT, scheduleInit);
window.addEventListener('online', retryGivenUpTiles);
